import { useMemo } from "react";
import {
  useTrainingRecords,
  useWeightRecords,
  type IPRRecord,
} from "./useAppState";
import { localTrainingDate } from "@/lib/memberTrainingDay";
import { trainingPRsInRange } from "@/lib/trainingRecordPRs";
function getWeekRange(date: Date): { start: string; end: string } {
  const monday = new Date(date);
  monday.setHours(0, 0, 0, 0);
  monday.setDate(
    monday.getDate() + (monday.getDay() === 0 ? -6 : 1 - monday.getDay()),
  );
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return { start: localTrainingDate(monday), end: localTrainingDate(sunday) };
}
export interface IWeeklyReportData {
  /** 周一日期 YYYY-MM-DD */
  weekStart: string;
  /** 周日日期 YYYY-MM-DD */
  weekEnd: string;
  /** 本周训练次数 */
  trainingCount: number;
  /** 总训练时长（分钟） */
  totalDurationMin: number;
  /** 总训练容量（重量 × 次数 累加，kg·次） */
  totalVolume: number;
  /** 计划总组数 */
  totalPlannedSets: number;
  /** 完成总组数 */
  totalCompletedSets: number;
  /** 训练完成度 0-1 */
  completionRate: number;
  /** 本周体重变化（kg，正数=增长，负数=下降；undefined 表示无数据） */
  weightChange: number | undefined;
  /** 本周初体重 */
  weekStartWeight: number | undefined;
  /** 本周末体重 */
  weekEndWeight: number | undefined;
  /** 本周打卡连击天数（本周内连续训练的天数最大值） */
  streakDaysThisWeek: number;
  /** 本周新增 PR 列表 */
  newPRs: IPRRecord[];
}

/** 计算指定周的周报数据 */
export function useWeeklyReport(weekStartDate: string) {
  const { records } = useTrainingRecords();
  const { weights } = useWeightRecords();

  const report = useMemo<IWeeklyReportData>(() => {
    const start = new Date(weekStartDate + "T00:00:00");
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    end.setHours(23, 59, 59, 999);
    const startStr = localTrainingDate(start);
    const endStr = localTrainingDate(end);

    // 本周训练记录
    const weekRecords = records.filter(
      (r) => r.date >= startStr && r.date <= endStr,
    );

    // 训练次数
    const uniqueDates = new Set(weekRecords.map((r) => r.date));
    const trainingCount = uniqueDates.size;

    // 总时长
    const totalDurationMin = weekRecords.reduce(
      (sum, r) => sum + r.durationMin,
      0,
    );

    // 总容量 & 计划/完成组数
    let totalVolume = 0;
    let totalPlannedSets = 0;
    let totalCompletedSets = 0;
    for (const rec of weekRecords) {
      totalPlannedSets += rec.totalSets || 0;
      totalCompletedSets += rec.completedSets || 0;
      for (const ex of rec.exercises) {
        for (const set of ex.sets) {
          if (set.completed && set.weight > 0) {
            totalVolume += set.weight * set.reps;
          }
        }
      }
    }

    const completionRate =
      totalPlannedSets > 0 ? totalCompletedSets / totalPlannedSets : 0;

    // 本周体重变化
    const weekWeights = weights
      .filter((w) => w.date >= startStr && w.date <= endStr)
      .sort((a, b) => a.date.localeCompare(b.date));
    let weekStartWeight: number | undefined;
    let weekEndWeight: number | undefined;
    let weightChange: number | undefined;
    if (weekWeights.length >= 2) {
      weekStartWeight = weekWeights[0].weight;
      weekEndWeight = weekWeights[weekWeights.length - 1].weight;
      weightChange = Number((weekEndWeight - weekStartWeight).toFixed(1));
    } else if (weekWeights.length === 1) {
      weekStartWeight = weekWeights[0].weight;
      weekEndWeight = weekWeights[0].weight;
      weightChange = 0;
    }

    // 本周连击天数（本周内从周一往周日算最长连续训练）
    let streakDaysThisWeek = 0;
    if (trainingCount > 0) {
      let currentStreak = 0;
      let maxStreak = 0;
      for (let i = 0; i < 7; i++) {
        const d = new Date(start);
        d.setDate(start.getDate() + i);
        const dateStr = localTrainingDate(d);
        if (uniqueDates.has(dateStr)) {
          currentStreak++;
          maxStreak = Math.max(maxStreak, currentStreak);
        } else {
          currentStreak = 0;
        }
      }
      streakDaysThisWeek = maxStreak;
    }

    // 本周新增 PR：recordDate 在本周内的 PR 记录
    const newPRs = trainingPRsInRange(records, startStr, endStr);

    return {
      weekStart: startStr,
      weekEnd: endStr,
      trainingCount,
      totalDurationMin,
      totalVolume,
      totalPlannedSets,
      totalCompletedSets,
      completionRate,
      weightChange,
      weekStartWeight,
      weekEndWeight,
      streakDaysThisWeek,
      newPRs,
    };
  }, [weekStartDate, records, weights]);

  return { report };
}

/** 获取当前周的周一日期（YYYY-MM-DD） */
export function getCurrentWeekMonday(): string {
  return getWeekRange(new Date()).start;
}

/** 上一周/下一周偏移 */
export function offsetWeek(weekStartStr: string, offsetWeeks: number): string {
  const d = new Date(weekStartStr + "T00:00:00");
  d.setDate(d.getDate() + offsetWeeks * 7);
  return localTrainingDate(d);
}
